import React, {
  useCallback, useEffect, useRef, useState,
} from 'react';
import {ChevronRightIcon} from '@heroicons/react-v2/24/solid';
import {useRouter} from 'next/router';
import _ from 'lodash';

import ModalNew from '../ModalNew';
import RecentSearches from './RecentSearches';
import SearchResults from './SearchResults';
import AceAvatar from '../chat/AceAvatar';
import {SearchIcon} from '../svg';
import {getSearchResults} from '../../api/api';
import {useAnalytics, useAppContext} from '../../hooks';
import {hideAIChatFeatures} from '../../util';

const RECENT_SEARCH_KEY = 'RECENT_SEARCHES';
const NUM_RECENT_SEARCHES = 6;

const SearchModal = ({isOpen, onClose, onClickResult = _.noop}) => {
  const [value, setValue] = useState('');
  const [results, setResults] = useState(null);
  const [recent, setRecent] = useState([]);
  const inputRef = useRef(null);
  const router = useRouter();
  const {tracker} = useAnalytics();
  const {currentUser} = useAppContext();
  const hideAskAce = hideAIChatFeatures(currentUser);

  // Initialize recent searches
  useEffect(() => {
    const recentSearches = JSON.parse(localStorage.getItem(RECENT_SEARCH_KEY)) || [];
    setRecent(recentSearches);
  }, [isOpen]);

  const completeSearch = useCallback(_.debounce(async (query) => {
    try {
      tracker.searchQuery({search: query});
      const newResults = await getSearchResults({query});
      // Ignore stale responses (query changed) / modal already closed (input unmounted).
      if (inputRef.current?.value !== query) return;
      setResults(newResults);
    } catch (err) {
      if (inputRef.current?.value !== query) return;
      setResults(null);
    }
  }, 500), []);

  const resetState = () => {
    completeSearch.cancel();
    setValue('');
    setResults(null);
  };

  const onChange = (e) => {
    const query = e.target.value;
    setValue(query);
    if (query) {
      completeSearch(query);
    } else {
      completeSearch.cancel();
      setResults(null);
    }
  };

  /**
   * @param {Object} result
   * @param {string} result.type
   * @param {string} result.href
   * @param {Object} result.item
   */
  const updateRecentSearches = (result) => {
    const recentSearches = JSON.parse(localStorage.getItem(RECENT_SEARCH_KEY)) || [];
    const newRecentSearches = [
      result,
      ...recentSearches.filter((search) => search.href !== result.href),
    ].slice(0, NUM_RECENT_SEARCHES);

    setRecent(newRecentSearches);
    localStorage.setItem(RECENT_SEARCH_KEY, JSON.stringify(newRecentSearches));
  };

  const clearRecentSearches = () => {
    setRecent([]);
    localStorage.removeItem(RECENT_SEARCH_KEY);
  };

  const handleClickResult = (result) => {
    tracker.searchClickResult({search: value, type: result.type, href: result.href});
    updateRecentSearches(result);
    onClickResult(result);
    onClose();
  };

  const handleClickRecentSearch = (result) => {
    tracker.searchClickRecent({search: value, type: result.type, href: result.href});
    updateRecentSearches(result);
    onClickResult(result);
    onClose();
  };

  const handleAskAce = () => {
    tracker.searchClickAskAce({search: value});
    onClose();
    router.push(value ? `/chat?q=${encodeURIComponent(value)}` : '/chat');
  };

  const onKeyDown = (e) => {
    if (e.key === 'Enter' && value && !hideAskAce) {
      e.preventDefault();
      handleAskAce();
    }
  };

  return (
    <ModalNew
      isOpen={isOpen}
      onClose={onClose}
      onCloseFinish={resetState}
      initialFocus={inputRef}
      className="relative z-50"
      centerVertically={false}
      modalStyle={{}}
      modalClassName="max-w-2xl sm:my-[10vh]"
    >
      {/* Search bar */}
      <div className="p-4">
        <div className="flex items-center gap-2 rounded-xl bg-gray-100 py-1.5 pl-4 pr-1.5 focus-within:ring-2 focus-within:ring-indigo-500">
          <SearchIcon className="h-5 w-5 shrink-0 pointer-events-none" />
          <input
            ref={inputRef}
            type="text"
            value={value}
            onChange={onChange}
            onKeyDown={onKeyDown}
            placeholder="Search or ask AI"
            aria-label="Search or ask AI"
            className="w-full min-w-0 border-none bg-transparent py-2 text-base text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-0"
          />
          {!hideAskAce && (
            <button
              type="button"
              onClick={handleAskAce}
              className="flex shrink-0 items-center whitespace-nowrap rounded-lg border border-indigo-700 bg-gradient-to-b from-indigo-500 to-indigo-700 px-2.5 py-2 text-sm font-semibold text-white shadow-sm transition hover:from-indigo-600 hover:to-indigo-800 cursor-pointer"
            >
              <AceAvatar className="h-5 w-5 border-none bg-none from-transparent to-transparent shadow-none mr-0.5" />
              <span className="sm:hidden">Ask AI</span>
              <span className="hidden sm:inline">Ask Ace</span>
              <ChevronRightIcon className="h-4 w-4 ml-1 -mr-1" />
            </button>
          )}
        </div>
      </div>

      {/* Body */}
      <div className="max-h-[65vh] overflow-y-auto px-4 pb-4">
        {results ? (
          <SearchResults
            results={results}
            query={value}
            onClick={handleClickResult}
            onAskAce={hideAskAce ? null : handleAskAce}
          />
        ) : (
          <>
            <div className="py-10 text-center">
              <SearchIcon stroke="#d1d5db" strokeWidth="2" className="mx-auto h-12 w-12" />
              <p className="mt-4 text-base text-gray-500">
                Search courses, interview questions, companies, guides, and more
              </p>
            </div>
            <RecentSearches
              recent={recent}
              onClick={handleClickRecentSearch}
              onClear={clearRecentSearches}
            />
          </>
        )}
      </div>
    </ModalNew>
  );
};

export default SearchModal;
